/**
 * webcam-recorder/processor.js — camera-frame processor.
 *
 * A Canvas-based pipeline that sits between the raw camera MediaStream and the
 * recorder. It performs, per output frame:
 *   1. aspect-crop (center-crop the source to '16:9' | '9:16' | '1:1'),
 *   2. optional horizontal mirror (flips the OUTPUT / recorded frame),
 *   3. optional background-blur via self-hosted MediaPipe Selfie Segmentation.
 *
 * Why a separate processor (not folded into recorder.js): the recorder is
 * source-agnostic — it encodes whatever MediaStream it is handed. This module
 * takes the raw camera stream, draws transformed frames onto an offscreen-ish
 * <canvas>, and exposes canvas.captureStream() as the *new* video source for
 * both the live preview (getCanvas) and the recorder (captureStream).
 *
 * Output canvas dimensions are always even (H.264 requires w/h divisible by 2).
 *
 * MediaPipe is self-hosted under /vendor/mediapipe/selfie_segmentation/ and
 * injected PAGE-RELATIVELY (so the RU shard is served from Beget and the intl
 * shard from its own origin — never an external CDN, which RU users can't reach).
 * Blur is lazy (loaded on first enable), throttled (~20 fps segmentation), and
 * fails GRACEFULLY: any load/init/runtime error → onBlurState('failed'), blur
 * silently disabled, plain crop+mirror draw continues. It never throws and
 * never freezes the preview.
 *
 * Public surface:
 *   var proc = new WebcamProcessor({
 *       source: MediaStream,          // raw camera stream (video-only)
 *       aspect: '16:9'|'9:16'|'1:1',  // default '16:9'
 *       mirror: false,                // flip horizontally in the OUTPUT frame
 *       blur: false,                  // background blur
 *       fps: 30,
 *       onBlurState: function(state){} // 'loading'|'ready'|'failed'|'off'
 *   });
 *   proc.getCanvas();        // the <canvas> (live preview in the stage)
 *   proc.captureStream();    // MediaStream from the canvas (fed to recorder)
 *   proc.setAspect(a); proc.setMirror(b); proc.setBlur(b);  // live updates
 *   proc.start(); proc.stop();
 */
(function () {
'use strict';
if (window.WebcamProcessor) return;

var MP_BASE = '/vendor/mediapipe/selfie_segmentation/';
var MP_SCRIPT = MP_BASE + 'selfie_segmentation.js';
var BLUR_LOAD_TIMEOUT_MS = 8000;   // hard cap on MediaPipe load + first result
var SEG_MIN_INTERVAL_MS = 50;      // throttle segmentation to ~20 fps
var BLUR_PX = 12;                  // background blur radius

// round to the nearest even integer ≥ 2 (H.264 needs w/h divisible by 2)
function roundEven(n) {
  var v = Math.round(n);
  if (v % 2 !== 0) v += 1;
  if (v < 2) v = 2;
  return v;
}

// Parse an aspect token into a {w,h} ratio. Unknown → 16:9.
function aspectRatio(token) {
  if (token === '9:16') return { w: 9, h: 16 };
  if (token === '1:1') return { w: 1, h: 1 };
  return { w: 16, h: 9 };
}

function WebcamProcessor(opts) {
  opts = opts || {};
  this._source = opts.source || null;
  this._aspect = opts.aspect || '16:9';
  this._mirror = !!opts.mirror;
  this._blurWanted = !!opts.blur;       // user intent
  this._blurActive = false;             // actually compositing blur right now
  this._fps = opts.fps && opts.fps > 0 ? opts.fps : 30;
  this._onBlurState = typeof opts.onBlurState === 'function'
    ? opts.onBlurState
    : function () {};
  // Optional post-draw overlay painter(ctx, W, H) — called after each output
  // frame is drawn (plain or blur composite), in identity (un-mirrored) canvas
  // space. Lets a host (air-draw) bake an overlay into BOTH the preview and the
  // recording. Default null → behaviour is byte-identical to before.
  this._overlay = (typeof opts.overlay === 'function') ? opts.overlay : null;

  // offscreen <video> draw source — NOT attached to the DOM
  this._video = document.createElement('video');
  this._video.muted = true;
  this._video.playsInline = true;
  this._video.setAttribute('playsinline', '');
  this._video.autoplay = true;

  // output canvas (the one we draw + record)
  this._canvas = document.createElement('canvas');
  this._ctx = this._canvas.getContext('2d');

  // dimensions (even); recomputed from source on start / resize / setAspect
  this._W = 2;
  this._H = 2;
  // last-computed center-crop source rect for the chosen aspect
  this._crop = { x: 0, y: 0, w: 0, h: 0 };

  this._rafId = 0;
  this._running = false;
  this._captureStream = null;

  // Frame-reader timing driver (MediaStreamTrackProcessor) — keeps the draw
  // loop alive when the tab is hidden / minimized, where rAF is throttled to
  // ~0 fps and would otherwise freeze the captureStream() on the last frame.
  this._frameReader = null;      // the MediaStreamTrackProcessor reader
  this._clonedTrack = null;      // cloned src video track (timing pulse only)
  this._readerActive = false;    // true while the frame-reader loop is driving

  // MediaPipe state
  this._selfieSeg = null;
  this._mpLoading = false;
  this._mpReady = false;
  this._mpFailed = false;
  this._lastSegTs = 0;
  this._segInFlight = false;
  this._loadTimer = 0;
  this._sawFirstResult = false;

  this._onResults = this._onResults.bind(this);
  this._tick = this._tick.bind(this);
}

// ── source / video wiring ──────────────────────────────────────────────

WebcamProcessor.prototype._attachSource = function () {
  if (!this._source) return;
  try {
    this._video.srcObject = this._source;
  } catch (e) {
    // older engines: fall back to URL.createObjectURL is not needed for streams
  }
  var p = this._video.play();
  if (p && typeof p.catch === 'function') {
    p.catch(function () { /* autoplay may reject silently; muted should allow */ });
  }
  // recompute dims once metadata is known, and on later track resizes
  var self = this;
  this._video.addEventListener('loadedmetadata', function () { self._recomputeDims(); });
  this._video.addEventListener('resize', function () { self._recomputeDims(); });

  // also react to track 'resize' (camera may renegotiate resolution)
  var tracks = (this._source.getVideoTracks && this._source.getVideoTracks()) || [];
  for (var i = 0; i < tracks.length; i++) {
    if (tracks[i].addEventListener) {
      tracks[i].addEventListener('resize', function () { self._recomputeDims(); });
    }
  }
};

// Source intrinsic dimensions (prefer real video frame size).
WebcamProcessor.prototype._sourceSize = function () {
  var sw = this._video.videoWidth || 0;
  var sh = this._video.videoHeight || 0;
  if (sw > 0 && sh > 0) return { w: sw, h: sh };
  // fall back to track settings if metadata not ready yet
  var tracks = (this._source && this._source.getVideoTracks && this._source.getVideoTracks()) || [];
  if (tracks.length && tracks[0].getSettings) {
    var s = tracks[0].getSettings();
    if (s && s.width && s.height) return { w: s.width, h: s.height };
  }
  return { w: 0, h: 0 };
};

// Compute the even output canvas size + the center-crop source rect.
WebcamProcessor.prototype._recomputeDims = function () {
  var src = this._sourceSize();
  if (src.w <= 0 || src.h <= 0) return; // not ready yet
  var sw = src.w, sh = src.h;
  var W, H, cropW, cropH;

  if (this._aspect === '1:1') {
    var side = roundEven(Math.min(sw, sh));
    W = side; H = side;
    cropW = Math.min(sw, sh);
    cropH = cropW;
  } else if (this._aspect === '9:16') {
    // portrait: full source height, width = round_even(height * 9/16)
    H = roundEven(sh);
    W = roundEven(sh * 9 / 16);
    cropH = sh;
    cropW = Math.min(sw, sh * 9 / 16);
    // if the source is narrower than the target portrait width, clamp by width
    if (cropW > sw) {
      cropW = sw;
      cropH = Math.min(sh, sw * 16 / 9);
    }
  } else {
    // '16:9' (default). With a 16:9 source this is a near-passthrough.
    var ar = aspectRatio(this._aspect);
    var targetAR = ar.w / ar.h; // 16/9
    var srcAR = sw / sh;
    if (Math.abs(srcAR - targetAR) < 0.01) {
      // near-passthrough — no letterbox, no crop
      cropW = sw; cropH = sh;
    } else if (srcAR > targetAR) {
      // source too wide → crop width
      cropH = sh;
      cropW = sh * targetAR;
    } else {
      // source too tall → crop height
      cropW = sw;
      cropH = sw / targetAR;
    }
    W = roundEven(cropW);
    H = roundEven(cropH);
  }

  // center the crop rect
  var cx = (sw - cropW) / 2;
  var cy = (sh - cropH) / 2;
  this._crop = { x: cx, y: cy, w: cropW, h: cropH };

  if (W !== this._W || H !== this._H) {
    this._W = W;
    this._H = H;
    this._canvas.width = W;
    this._canvas.height = H;
  }
};

// ── public API ─────────────────────────────────────────────────────────

WebcamProcessor.prototype.getCanvas = function () {
  return this._canvas;
};

WebcamProcessor.prototype.captureStream = function () {
  if (this._captureStream) return this._captureStream;
  if (this._canvas.captureStream) {
    this._captureStream = this._canvas.captureStream(this._fps);
  } else if (this._canvas.mozCaptureStream) {
    this._captureStream = this._canvas.mozCaptureStream(this._fps);
  } else {
    this._captureStream = null;
  }
  return this._captureStream;
};

WebcamProcessor.prototype.setAspect = function (a) {
  if (a !== '16:9' && a !== '9:16' && a !== '1:1') return;
  this._aspect = a;
  this._recomputeDims();
};

WebcamProcessor.prototype.setMirror = function (b) {
  this._mirror = !!b;
};

WebcamProcessor.prototype.setOverlay = function (fn) {
  this._overlay = (typeof fn === 'function') ? fn : null;
};

// Center-crop rect in SOURCE-normalized coords (0..1). Lets a host map raw
// camera-normalized points into this processor's cropped output space.
WebcamProcessor.prototype.getCropNorm = function () {
  var s = this._sourceSize();
  if (!s.w || !s.h || !this._crop.w) return { x: 0, y: 0, w: 1, h: 1 };
  return { x: this._crop.x / s.w, y: this._crop.y / s.h, w: this._crop.w / s.w, h: this._crop.h / s.h };
};

// Paint the optional overlay in identity (un-mirrored) output px space.
WebcamProcessor.prototype._paintOverlay = function () {
  if (!this._overlay) return;
  try { this._overlay(this._ctx, this._W, this._H); } catch (e) {}
};

WebcamProcessor.prototype.setBlur = function (b) {
  b = !!b;
  if (b === this._blurWanted) return;
  this._blurWanted = b;
  if (b) {
    // user wants blur on
    if (this._mpFailed) {
      // a prior attempt failed permanently — don't retry, stay plain
      this._blurWanted = false;
      this._onBlurState('failed');
      return;
    }
    if (this._mpReady) {
      this._blurActive = true;
      this._onBlurState('ready');
    } else {
      this._ensureMediaPipe(); // lazy load; fires 'loading' then 'ready'/'failed'
    }
  } else {
    // turn blur off — keep the loaded model for fast re-enable
    this._blurActive = false;
    this._segInFlight = false;
    this._onBlurState('off');
  }
};

WebcamProcessor.prototype.start = function () {
  if (this._running) return;
  this._running = true;
  this._attachSource();
  this._recomputeDims();
  if (this._blurWanted && !this._mpReady && !this._mpFailed) {
    this._ensureMediaPipe();
  } else if (this._blurWanted && this._mpReady) {
    this._blurActive = true;
  }
  // Prefer the MediaStreamTrackProcessor frame-reader as the draw TIMING source:
  // it delivers frames at the camera's cadence even when the tab is hidden,
  // unlike rAF (throttled to ~0 fps when backgrounded). Fall back to rAF only
  // when MediaStreamTrackProcessor is unavailable (Firefox/Safari).
  if (!this._startFrameReader()) {
    this._rafId = requestAnimationFrame(this._tick);
  }
};

// Try to drive the draw loop from a MediaStreamTrackProcessor on a CLONE of the
// source video track. Returns true if the frame-reader loop was started (in
// which case the rAF loop must NOT also run — no double-draw). Returns false
// (so the caller falls back to rAF) if MediaStreamTrackProcessor is absent or
// no usable source track exists. Each read() yields one frame used purely as a
// timing pulse — we close it immediately and let the existing draw routine pull
// from the offscreen <video> exactly as before.
WebcamProcessor.prototype._startFrameReader = function () {
  if (typeof window.MediaStreamTrackProcessor === 'undefined') return false;
  var tracks = (this._source && this._source.getVideoTracks &&
                this._source.getVideoTracks()) || [];
  var srcVideoTrack = tracks.length ? tracks[0] : null;
  if (!srcVideoTrack || typeof srcVideoTrack.clone !== 'function') return false;

  var clone, processor, reader;
  try {
    // Clone so the ORIGINAL track stays fully consumable by the offscreen
    // <video> (the clone is an independent track on the same source).
    clone = srcVideoTrack.clone();
    processor = new window.MediaStreamTrackProcessor({ track: clone });
    reader = processor.readable.getReader();
  } catch (e) {
    if (clone) { try { clone.stop(); } catch (e2) {} }
    return false;
  }

  this._clonedTrack = clone;
  this._frameReader = reader;
  this._readerActive = true;

  var self = this;
  function pump() {
    if (!self._readerActive || self._frameReader !== reader) return;
    reader.read().then(function (res) {
      if (!self._readerActive || self._frameReader !== reader) {
        if (res && res.value) { try { res.value.close(); } catch (e) {} }
        return;
      }
      if (res.done) {
        // track ended — stop driving from the reader (do NOT silently freeze:
        // if we're still running, fall back to rAF so the canvas keeps updating)
        self._readerActive = false;
        if (self._running && !self._rafId) {
          self._rafId = requestAnimationFrame(self._tick);
        }
        return;
      }
      // The frame is ONLY a timing pulse — the draw routine keeps pulling from
      // the offscreen <video>. Close it immediately to avoid back-pressure.
      if (res.value) { try { res.value.close(); } catch (e) {} }
      // Run the SAME draw step the rAF tick would (crop+mirror or blur), guarded.
      try { self._drawStep(); } catch (e) {}
      pump();
    }).catch(function () {
      // reader cancelled or errored. If this is still the active reader, stop the
      // reader loop and fall back to rAF (only when we're still meant to run — a
      // cancel from stop()/teardown nulls _frameReader, so we won't re-arm then).
      if (self._frameReader === reader) {
        self._readerActive = false;
        if (self._running && !self._rafId) {
          self._rafId = requestAnimationFrame(self._tick);
        }
      }
    });
  }
  pump();
  return true;
};

// Tear down the frame-reader driver: stop the loop, cancel the reader, stop the
// cloned timing track. Idempotent — safe to call when no reader is active and
// safe to call repeatedly (stop() + rebuild). Leaves no dangling reader/track.
WebcamProcessor.prototype._stopFrameReader = function () {
  this._readerActive = false;
  var reader = this._frameReader;
  this._frameReader = null;
  if (reader) {
    try {
      var p = reader.cancel();
      if (p && typeof p.catch === 'function') { p.catch(function () {}); }
    } catch (e) {}
  }
  if (this._clonedTrack) {
    try { this._clonedTrack.stop(); } catch (e) {}
    this._clonedTrack = null;
  }
};

WebcamProcessor.prototype.stop = function () {
  this._running = false;
  // tear down the frame-reader driver (if it was active) — cancel the reader,
  // stop the cloned timing track. Do this BEFORE clearing rAF so no second loop
  // can be re-armed by a late read() resolution.
  this._stopFrameReader();
  if (this._rafId) {
    cancelAnimationFrame(this._rafId);
    this._rafId = 0;
  }
  if (this._loadTimer) {
    clearTimeout(this._loadTimer);
    this._loadTimer = 0;
  }
  // stop the canvas capture tracks
  if (this._captureStream) {
    var ct = this._captureStream.getTracks();
    for (var i = 0; i < ct.length; i++) {
      try { ct[i].stop(); } catch (e) {}
    }
    this._captureStream = null;
  }
  // detach the offscreen video
  try { this._video.pause(); } catch (e) {}
  try { this._video.srcObject = null; } catch (e) {}
  // tear down MediaPipe
  if (this._selfieSeg && typeof this._selfieSeg.close === 'function') {
    try { this._selfieSeg.close(); } catch (e) {}
  }
  this._selfieSeg = null;
  this._mpReady = false;
  this._blurActive = false;
  this._segInFlight = false;
};

// ── draw loop ──────────────────────────────────────────────────────────

WebcamProcessor.prototype._tick = function () {
  if (!this._running) return;
  // If the frame-reader took over as the timing driver, stop the rAF loop here
  // (no double-draw). This also covers a stray queued rAF after _startFrameReader.
  if (this._readerActive) { this._rafId = 0; return; }
  // schedule next frame first so an exception mid-draw can't kill the loop
  this._rafId = requestAnimationFrame(this._tick);
  this._drawStep();
};

// One draw step, shared by the rAF tick and the frame-reader loop. Contains the
// SAME crop+mirror / blur logic as before — only the caller (timing) differs.
WebcamProcessor.prototype._drawStep = function () {
  if (!this._running) return;
  // make sure we have valid dims (camera may have only just produced metadata)
  if (this._W <= 2 || this._H <= 2 || this._crop.w <= 0) {
    this._recomputeDims();
  }
  if (this._W <= 0 || this._H <= 0 || this._crop.w <= 0) return;
  if (this._video.readyState < 2) return; // HAVE_CURRENT_DATA

  if (this._blurActive && this._selfieSeg && this._mpReady) {
    this._drawBlur();
  } else {
    this._drawPlain();
  }
};

// plain center-crop (+ optional mirror) draw
WebcamProcessor.prototype._drawPlain = function () {
  var ctx = this._ctx;
  var W = this._W, H = this._H;
  var c = this._crop;
  ctx.save();
  if (this._mirror) {
    ctx.translate(W, 0);
    ctx.scale(-1, 1);
  }
  try {
    ctx.drawImage(this._video, c.x, c.y, c.w, c.h, 0, 0, W, H);
  } catch (e) {
    // drawImage can throw if the frame isn't decodable yet — skip this frame
  }
  ctx.restore();
  this._paintOverlay();
};

// background-blur draw: throttle segmentation, composite happens in _onResults
WebcamProcessor.prototype._drawBlur = function () {
  var now = (window.performance && performance.now) ? performance.now() : Date.now();
  if (this._segInFlight) return;            // wait for the in-flight result
  if (now - this._lastSegTs < SEG_MIN_INTERVAL_MS) {
    // between segmentation frames — keep preview live by repainting last result
    // (the composite already left a frame on the canvas; nothing to do)
    return;
  }
  this._lastSegTs = now;
  this._segInFlight = true;
  var self = this;
  // send() returns a promise; guard every failure mode → fall back to plain
  var ran = false;
  try {
    var ret = this._selfieSeg.send({ image: this._video });
    if (ret && typeof ret.then === 'function') {
      ran = true;
      ret.then(function () {
        self._segInFlight = false;
      }).catch(function (err) {
        self._segInFlight = false;
        self._failBlur(err);
      });
    }
  } catch (err) {
    this._segInFlight = false;
    this._failBlur(err);
    return;
  }
  if (!ran) {
    // send() was synchronous / returned no promise — clear the flag next tick
    this._segInFlight = false;
  }
};

// MediaPipe results callback — composite sharp person over blurred background
WebcamProcessor.prototype._onResults = function (results) {
  if (!this._sawFirstResult) {
    this._sawFirstResult = true;
    this._mpReady = true;
    this._mpLoading = false;
    if (this._loadTimer) { clearTimeout(this._loadTimer); this._loadTimer = 0; }
    if (this._blurWanted) {
      this._blurActive = true;
      this._onBlurState('ready');
    }
  }
  // if blur was toggled off while a result was in flight, ignore it
  if (!this._blurActive) return;
  if (!results || !results.image || !results.segmentationMask) {
    // malformed result — degrade to plain for this frame
    this._drawPlain();
    return;
  }

  var octx = this._ctx;
  var W = this._W, H = this._H;
  var c = this._crop;

  try {
    octx.save();
    if (this._mirror) {
      octx.translate(W, 0);
      octx.scale(-1, 1);
    }
    octx.clearRect(0, 0, W, H);

    // 1. draw the person mask, cropped to our aspect rect
    octx.drawImage(results.segmentationMask, c.x, c.y, c.w, c.h, 0, 0, W, H);

    // 2. keep only the masked (person) pixels of the sharp frame
    octx.globalCompositeOperation = 'source-in';
    octx.drawImage(results.image, c.x, c.y, c.w, c.h, 0, 0, W, H);

    // 3. paint the blurred full frame BEHIND the person
    octx.globalCompositeOperation = 'destination-over';
    octx.filter = 'blur(' + BLUR_PX + 'px)';
    octx.drawImage(results.image, c.x, c.y, c.w, c.h, 0, 0, W, H);
    octx.filter = 'none';

    octx.globalCompositeOperation = 'source-over';
    octx.restore();
    this._paintOverlay();
  } catch (err) {
    // composite failed (e.g. filter unsupported) — reset state, go plain
    try {
      octx.filter = 'none';
      octx.globalCompositeOperation = 'source-over';
      octx.restore();
    } catch (e2) {}
    this._failBlur(err);
    this._drawPlain();
  }
};

// ── MediaPipe lazy load + graceful failure ─────────────────────────────

WebcamProcessor.prototype._ensureMediaPipe = function () {
  if (this._mpReady) {
    this._blurActive = !!this._blurWanted;
    if (this._blurActive) this._onBlurState('ready');
    return;
  }
  if (this._mpFailed) {
    this._onBlurState('failed');
    return;
  }
  if (this._mpLoading) return; // already in progress
  this._mpLoading = true;
  this._sawFirstResult = false;
  this._onBlurState('loading');

  var self = this;
  // hard timeout: if we don't get a first result within the budget, fail over
  this._loadTimer = setTimeout(function () {
    if (!self._mpReady) {
      self._failBlur(new Error('mediapipe load/first-result timeout'));
    }
  }, BLUR_LOAD_TIMEOUT_MS);

  this._injectScript(MP_SCRIPT, function (err) {
    if (err) { self._failBlur(err); return; }
    self._initSelfieSeg();
  });
};

// inject the self-hosted MediaPipe script PAGE-RELATIVELY (never a CDN)
WebcamProcessor.prototype._injectScript = function (src, cb) {
  // already present? (re-enable after off, or a prior partial load)
  if (window.SelfieSegmentation) { cb(null); return; }
  var existing = document.querySelector('script[data-mp-selfieseg="1"]');
  if (existing) {
    if (existing.getAttribute('data-mp-loaded') === '1') { cb(null); return; }
    existing.addEventListener('load', function () { cb(null); });
    existing.addEventListener('error', function () { cb(new Error('mediapipe script load error')); });
    return;
  }
  var s = document.createElement('script');
  s.src = src;            // PAGE-RELATIVE → served from our own origin
  s.async = true;
  s.crossOrigin = 'anonymous';
  s.setAttribute('data-mp-selfieseg', '1');
  s.addEventListener('load', function () {
    s.setAttribute('data-mp-loaded', '1');
    cb(null);
  });
  s.addEventListener('error', function () {
    cb(new Error('mediapipe script load error'));
  });
  (document.head || document.documentElement).appendChild(s);
};

WebcamProcessor.prototype._initSelfieSeg = function () {
  var SelfieSegmentation = window.SelfieSegmentation;
  if (typeof SelfieSegmentation !== 'function') {
    this._failBlur(new Error('SelfieSegmentation global missing'));
    return;
  }
  var self = this;
  try {
    var seg = new SelfieSegmentation({
      locateFile: function (f) { return MP_BASE + f; } // self-hosted assets
    });
    seg.setOptions({ modelSelection: 1 });
    seg.onResults(this._onResults);
    this._selfieSeg = seg;
    // kick a first send so onResults fires (warm-up). Guard everything.
    // The draw loop will also drive send()s, but firing one here gets us to
    // 'ready' even if the loop hasn't reached a blur frame yet.
    if (this._video.readyState >= 2 && this._video.videoWidth > 0) {
      this._segInFlight = true;
      this._lastSegTs = (window.performance && performance.now) ? performance.now() : Date.now();
      var r = seg.send({ image: this._video });
      if (r && typeof r.then === 'function') {
        r.then(function () { self._segInFlight = false; })
         .catch(function (err) { self._segInFlight = false; self._failBlur(err); });
      } else {
        this._segInFlight = false;
      }
    }
    // else: not ready yet — the draw loop's _drawBlur will fire the first send
  } catch (err) {
    this._failBlur(err);
  }
};

// Permanent, graceful blur failure: never throw, never freeze. Switch to plain.
WebcamProcessor.prototype._failBlur = function (err) {
  if (this._mpFailed && !this._blurActive) {
    // already failed and already plain — nothing to do
    return;
  }
  this._mpFailed = true;
  this._mpReady = false;
  this._mpLoading = false;
  this._blurActive = false;
  this._segInFlight = false;
  if (this._loadTimer) { clearTimeout(this._loadTimer); this._loadTimer = 0; }
  // best-effort teardown of a half-initialised model
  if (this._selfieSeg && typeof this._selfieSeg.close === 'function') {
    try { this._selfieSeg.close(); } catch (e) {}
  }
  this._selfieSeg = null;
  if (window.console && console.warn) {
    console.warn('[WebcamProcessor] background blur unavailable, using plain crop+mirror:', err);
  }
  this._onBlurState('failed');
  // the draw loop continues on the next rAF tick with _drawPlain()
};

window.WebcamProcessor = WebcamProcessor;
})();
